import { useState, useMemo } from 'react';
import type { Integration, ActivityEvent, SourceKind } from '../../types';
import { ScreenFrame } from '../layout/ScreenFrame';
import { PageHeader } from '../layout/PageHeader';
import { SourceIcon, StatusDot, ScopePill, NavIcon } from '../primitives';
import { useDocuments, useSync } from '../../hooks/useApi';
import { relTime } from '../../utils/time';

const USER_ID = 'adam';

type IntegrationConfig = Omit<Integration, 'counts'>;

const INTEGRATIONS_CONFIG: IntegrationConfig[] = [
  {
    id: 'slack',
    name: 'Slack',
    desc: 'Channels you select + your DMs. DMs stay private to you.',
    status: 'connected',
    connectedAs: 'fairmint.slack.com',
    lastSync: 'live',
    syncing: false,
    scope: 'workspace',
    config: [
      { label: 'Workspace', value: 'fairmint.slack.com' },
      { label: 'Channels',  value: '12 of 47 selected'  },
      { label: 'DMs',       value: 'Yours only (private)' },
      { label: 'Backfill',  value: 'Last 180 days'       },
    ],
  },
  {
    id: 'zoom',
    name: 'Zoom',
    desc: 'Cloud recordings + auto-generated transcripts.',
    status: 'connected',
    connectedAs: 'andrew@fairmint.co',
    lastSync: 'live',
    syncing: false,
    scope: 'mixed',
    config: [
      { label: 'Account',     value: 'andrew@fairmint.co'   },
      { label: 'Recordings',  value: 'All cloud recordings' },
      { label: 'Transcripts', value: 'Enabled'              },
      { label: 'Backfill',    value: 'Last 90 days'         },
    ],
  },
  {
    id: 'fathom',
    name: 'Fathom',
    desc: 'AI meeting recordings, transcripts, and summaries.',
    status: 'connected',
    connectedAs: 'fairmint',
    lastSync: 'live',
    syncing: false,
    scope: 'workspace',
    config: [
      { label: 'Workspace', value: 'fairmint'         },
      { label: 'Webhooks',  value: 'Live (real-time)' },
    ],
  },
  {
    id: 'linear',
    name: 'Linear',
    desc: 'Tickets, comments, status changes, and relationships.',
    status: 'connected',
    connectedAs: 'fairmint',
    lastSync: 'live',
    syncing: false,
    scope: 'workspace',
    config: [
      { label: 'Workspace', value: 'fairmint'                      },
      { label: 'Teams',     value: 'Engineering, Product, GTM'     },
      { label: 'Webhooks',  value: 'Live (real-time)'              },
    ],
  },
  {
    id: 'kb',
    name: 'Knowledge Base',
    desc: 'Notion pages: handbook, specs, RFCs, customer docs.',
    status: 'connected',
    connectedAs: 'fairmint.notion.so',
    lastSync: 'live',
    syncing: false,
    scope: 'workspace',
    config: [
      { label: 'Workspace', value: 'fairmint.notion.so'         },
      { label: 'Pages',     value: 'Engineering + Company spaces' },
      { label: 'Refresh',   value: 'Every 15 min'               },
    ],
  },
  {
    id: 'gmail',
    name: 'Gmail',
    desc: 'Your inbox — stays private to you only.',
    status: 'disconnected',
    scope: 'personal',
  },
];

interface IntegrationCardProps {
  integration: Integration;
  onSync: (source: string, cb: (result: { inserted_or_updated?: number; error?: string }) => void) => void;
  syncing: boolean;
}

function IntegrationCard({ integration: i, onSync, syncing }: IntegrationCardProps) {
  const [open, setOpen] = useState(false);
  const [syncResult, setSyncResult] = useState<{ inserted_or_updated?: number; error?: string } | null>(null);
  const connected = i.status !== 'disconnected';
  const isSyncing = i.syncing || syncing;

  return (
    <div
      className="integration-card card-surface"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        opacity: connected ? 1 : 0.92,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          className="card-icon-bg"
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: 'var(--surface-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SourceIcon kind={i.id} size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>{i.name}</div>
            <ScopePill scope={i.scope} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2, lineHeight: 1.4 }}>{i.desc}</div>
        </div>
      </div>

      {/* Status row */}
      <div
        className="surface-inner"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          borderRadius: 10,
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
        }}
      >
        <StatusDot status={isSyncing ? 'syncing' : i.status} />
        <div style={{ flex: 1, fontSize: 12 }}>
          {connected ? (
            <>
              <span style={{ fontWeight: 600 }}>{isSyncing ? 'Syncing…' : 'Connected'}</span>
              <span style={{ color: 'var(--slate)' }}> · {i.connectedAs}</span>
            </>
          ) : (
            <span style={{ color: 'var(--slate)' }}>Not connected</span>
          )}
        </div>
        {connected && (
          <span style={{ fontSize: 11, color: 'var(--slate-light)', fontFamily: 'var(--font-mono)' }}>
            {isSyncing ? 'in progress' : `synced ${i.lastSync}`}
          </span>
        )}
      </div>

      {/* Counts */}
      {connected && i.counts && (
        <div style={{ display: 'flex', gap: 18 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Total items</div>
            <div className="stat-number" style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
              {i.counts.items.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Today</div>
            <div className="stat-number-accent" style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 2, fontVariantNumeric: 'tabular-nums', color: 'var(--blue)' }}>
              +{i.counts.today}
            </div>
          </div>
        </div>
      )}

      {/* Config (expandable) */}
      {connected && open && i.config && (
        <div
          style={{
            marginTop: 4,
            padding: 12,
            borderRadius: 10,
            background: 'var(--surface)',
            border: '1px solid var(--line-soft)',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px 16px',
          }}
        >
          {i.config.map((c, idx) => (
            <div key={idx} style={{ fontSize: 12, minWidth: 0 }}>
              <div style={{ color: 'var(--slate)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{c.label}</div>
              <div style={{ color: 'var(--deep)', fontWeight: 500, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        {connected ? (
          <>
            <button
              onClick={() => setOpen(!open)}
              style={{
                flex: 1,
                height: 34,
                border: '1px solid var(--line)',
                background: 'var(--white)',
                borderRadius: 8,
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--deep)',
                cursor: 'pointer',
              }}
            >
              {open ? 'Hide config' : 'Configure'}
            </button>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button
                onClick={() => { setSyncResult(null); onSync(i.id, setSyncResult); }}
                disabled={isSyncing}
                style={{
                  width: '100%',
                  height: 34,
                  border: '1px solid var(--line)',
                  background: 'var(--white)',
                  borderRadius: 8,
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 600,
                  color: isSyncing ? 'var(--slate)' : 'var(--deep)',
                  cursor: isSyncing ? 'not-allowed' : 'pointer',
                }}
              >
                {isSyncing ? 'Syncing…' : 'Resync now'}
              </button>
              {syncResult && (
                <div style={{
                  fontSize: 11,
                  padding: '4px 8px',
                  borderRadius: 6,
                  background: syncResult.error ? 'rgba(239,68,68,.08)' : 'rgba(16,185,129,.08)',
                  color: syncResult.error ? '#ef4444' : '#10b981',
                  fontFamily: 'var(--font-mono)',
                }}>
                  {syncResult.error
                    ? `Error: ${syncResult.error}`
                    : `✓ ${syncResult.inserted_or_updated ?? 0} meetings synced`}
                </div>
              )}
            </div>
          </>
        ) : (
          <button
            className="btn-brand"
            style={{
              flex: 1,
              height: 34,
              border: 'none',
              background: 'var(--gradient-brand)',
              color: 'white',
              borderRadius: 8,
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Connect {i.name}
          </button>
        )}
      </div>
    </div>
  );
}

interface PipelineProps {
  total: number;
  today: number;
  connectedCount: number;
  totalSources: number;
  isLoading: boolean;
}

function IngestionPipeline({ total, today, connectedCount, totalSources, isLoading }: PipelineProps) {
  return (
    <div
      className="pipeline-bar card-surface"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 28,
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>Total ingested</div>
        <div className="stat-number" style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {isLoading ? '—' : total.toLocaleString()}
        </div>
      </div>
      <div style={{ width: 1, height: 44, background: 'var(--line)' }} />
      <div>
        <div style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>Added today</div>
        <div className="stat-number-accent" style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: 'var(--blue)' }}>
          {isLoading ? '—' : `+${today}`}
        </div>
      </div>
      <div style={{ width: 1, height: 44, background: 'var(--line)' }} />
      <div>
        <div style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>Sources</div>
        <div className="stat-number" style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {connectedCount}<span style={{ color: 'var(--slate-light)', fontWeight: 600 }}> / {totalSources}</span>
        </div>
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--slate)' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: 'var(--success)', boxShadow: '0 0 0 3px rgba(16,185,129,.18)' }} />
        Pipeline healthy
      </div>
    </div>
  );
}

interface ActivityProps {
  events: ActivityEvent[];
  isLoading: boolean;
}

function RecentActivity({ events, isLoading }: ActivityProps) {
  return (
    <div
      className="card-surface"
      style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: 16, overflow: 'hidden' }}
    >
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--line-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.005em' }}>Ingestion log</div>
        <span style={{ fontSize: 11, color: 'var(--slate)', fontFamily: 'var(--font-mono)' }}>live</span>
      </div>
      <div style={{ maxHeight: 420, overflow: 'auto' }}>
        {isLoading ? (
          <div style={{ padding: '20px 18px', fontSize: 13, color: 'var(--slate)' }}>Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ padding: '20px 18px', fontSize: 13, color: 'var(--slate)' }}>No recent activity.</div>
        ) : (
          events.map((e, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 18px',
                borderBottom: i < events.length - 1 ? '1px solid var(--line-soft)' : 'none',
              }}
            >
              <SourceIcon kind={e.src} size={16} />
              <div
                style={{
                  flex: 1,
                  fontSize: 13,
                  color: 'var(--deep)',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {e.text}
              </div>
              <div style={{ fontSize: 11, color: 'var(--slate-light)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                {e.t}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

interface Props {
  onNav: (screen: string) => void;
}

export function SourcesScreen({ onNav }: Props) {
  const { data: allDocs, isLoading, refetch } = useDocuments(USER_ID);
  const syncMutation = useSync(USER_ID);

  const handleSync = (source: string, cb: (r: { inserted_or_updated?: number; error?: string }) => void) => {
    syncMutation.mutate(source, {
      onSuccess: (data) => { cb(data); refetch(); },
      onError: (err) => cb({ error: err.message }),
    });
  };

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const sourceCounts = useMemo(() => {
    const counts: Record<string, { items: number; today: number }> = {};
    for (const doc of allDocs ?? []) {
      if (!counts[doc.source]) counts[doc.source] = { items: 0, today: 0 };
      counts[doc.source].items++;
      if (new Date(doc.occurred_at).getTime() >= todayStart) {
        counts[doc.source].today++;
      }
    }
    return counts;
  }, [allDocs, todayStart]);

  const integrations: Integration[] = useMemo(
    () =>
      INTEGRATIONS_CONFIG.map((cfg) => ({
        ...cfg,
        counts: sourceCounts[cfg.id],
      })),
    [sourceCounts],
  );

  const connected = integrations.filter((i) => i.status !== 'disconnected');
  const total = allDocs?.length ?? 0;
  const today = useMemo(
    () => (allDocs ?? []).filter((d) => new Date(d.occurred_at).getTime() >= todayStart).length,
    [allDocs, todayStart],
  );

  const recentActivity: ActivityEvent[] = useMemo(() => {
    return (allDocs ?? []).slice(0, 20).map((doc) => {
      const m = doc.metadata;
      const title =
        (m.title as string) ||
        (m.subject as string) ||
        (m.name as string) ||
        doc.source;
      return {
        src: doc.source as SourceKind,
        text: title,
        t: relTime(doc.occurred_at),
      };
    });
  }, [allDocs]);

  return (
    <ScreenFrame>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: .4 } }`}</style>

      <PageHeader
        kicker="MVP · Step 1"
        title="Connect your sources"
        meta={<span>Get data flowing in. Once sources are connected, you can search, browse, and ask questions.</span>}
        actions={(
          <button
            className="btn-primary"
            onClick={() => onNav('stream')}
            style={{
              height: 38,
              padding: '0 16px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--deep)',
              color: 'white',
              border: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            View ingested data
            <NavIcon name="arrow" size={14} />
          </button>
        )}
      />

      <div style={{ marginBottom: 20 }}>
        <IngestionPipeline
          total={total}
          today={today}
          connectedCount={connected.length}
          totalSources={integrations.length}
          isLoading={isLoading}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 12 }}>
            Data sources
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {integrations.map((i) => (
              <IntegrationCard
                key={i.id}
                integration={i}
                onSync={handleSync}
                syncing={syncMutation.isPending && syncMutation.variables === i.id as string}
              />
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 12 }}>
            Live activity
          </div>
          <RecentActivity events={recentActivity} isLoading={isLoading} />

          <div
            style={{
              marginTop: 16,
              padding: 16,
              borderRadius: 14,
              background: 'var(--surface-2)',
              border: '1px dashed var(--line)',
              fontSize: 12,
              color: 'var(--slate)',
              lineHeight: 1.55,
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--deep)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 6 }}>
              MVP scope
            </div>
            Step 1 (now): connect sources, verify data ingestion.
            <br />Step 2: browse + search across everything.
            <br />Step 3: ask AI questions, get answers with citations.
            <br /><br />
            Personal silos (My Brain), org synthesis (Company Brain), digests, and graph views are deferred — once data is flowing reliably, we iterate.
          </div>
        </div>
      </div>
    </ScreenFrame>
  );
}
